import CompanyCell from '@app/src/components/Table/Cells/CompanyCell'
import type { Provider } from '@app/src/types/organizations'
import { CheckCircleOutlined, ExpandMoreOutlined, InfoOutlined } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

type EnrichmentEligibilityStepProps = {
  eligibleProviders: Provider[]
  alreadyEnrichedProviders: Provider[]
  selectedProviderIds: number[]
  onProviderToggle: (providerId: number) => void
}

const EnrichmentEligibilityStep: React.FC<EnrichmentEligibilityStepProps> = ({
  eligibleProviders,
  alreadyEnrichedProviders,
  selectedProviderIds,
  onProviderToggle,
}) => {
  const { formatMessage } = useIntl()

  const cannotEnrichCount = alreadyEnrichedProviders.length
  const totalCount = eligibleProviders.length + cannotEnrichCount

  return (
    <Stack spacing={6}>
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Typography color="textSecondary">
            {formatMessage({ id: 'form.scheduleEnrichment.eligibility.companiesSelected' }, { count: totalCount })}
          </Typography>
        </Stack>

        {/* Can be enriched */}
        {eligibleProviders.length > 0 && (
          <Accordion
            elevation={0}
            disableGutters
            defaultExpanded
            sx={{ bgcolor: 'grey.100', '&:before': { position: 'unset' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreOutlined fontSize="medium" sx={{ mx: 1 }} />} sx={{ py: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <CheckCircleOutlined sx={{ color: '#28947D' }} />
                <Typography>
                  {formatMessage(
                    { id: 'form.scheduleEnrichment.eligibility.canBeEnriched' },
                    { count: eligibleProviders.length },
                  )}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List disablePadding>
                {eligibleProviders.map(provider => (
                  <ListItem
                    key={provider.id}
                    sx={{
                      justifyContent: 'space-between',
                      borderTop: `1px solid ${alpha('#000', 0.12)}`,
                    }}
                    disablePadding
                  >
                    <ListItemButton
                      selected={selectedProviderIds.includes(provider.id)}
                      onClick={() => onProviderToggle(provider.id)}
                    >
                      <Stack direction="row" alignItems="center" gap={1} width="100%">
                        <Checkbox edge="start" checked={selectedProviderIds.includes(provider.id)} />
                        <CompanyCell company={provider} disableCell />
                      </Stack>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        )}
      </Stack>

      {/* Cannot be enriched section */}
      <Stack spacing={2}>
        {cannotEnrichCount > 0 && (
          <>
            <Stack spacing={1}>
              <Typography color="textSecondary">
                {formatMessage(
                  { id: 'form.scheduleEnrichment.eligibility.cannotBeEnriched' },
                  { count: cannotEnrichCount },
                )}
              </Typography>
            </Stack>
            {/* Already enriched */}
            {alreadyEnrichedProviders.length > 0 && (
              <Accordion elevation={0} disableGutters sx={{ bgcolor: 'grey.100', '&:before': { position: 'unset' } }}>
                <AccordionSummary expandIcon={<ExpandMoreOutlined fontSize="medium" sx={{ mx: 1 }} />} sx={{ py: 1 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <InfoOutlined sx={{ color: 'text.secondary' }} />
                    <Stack>
                      <Typography>
                        {formatMessage(
                          { id: 'form.scheduleEnrichment.eligibility.alreadyEnriched' },
                          { count: alreadyEnrichedProviders.length },
                        )}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatMessage({ id: 'form.scheduleEnrichment.eligibility.alreadyEnrichedDescription' })}
                      </Typography>
                    </Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List disablePadding>
                    {alreadyEnrichedProviders.map(provider => (
                      <ListItem
                        key={provider.id}
                        sx={{
                          justifyContent: 'space-between',
                          borderTop: `1px solid ${alpha('#000', 0.12)}`,
                          py: 1.5,
                          px: 2,
                        }}
                      >
                        <CompanyCell company={provider} disableCell />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}
      </Stack>
    </Stack>
  )
}

export default EnrichmentEligibilityStep
